// /tenants/signups/:id (a signup's own id; old /signups/:id links redirect
// here, App.tsx). A signup is a tenant (owner, November 3): while its league
// exists this opens the tenant page's Signup tab. Once the league is deleted
// (by the cleanup, or from Ops) it's a deleted tenant, under Tenants › Deleted,
// and this page keeps everything about it: who signed up, where they came
// from, their organisation, the Growth lead, the notes and every email, and
// the compose panel to write to them again.

import { Navigate, useParams } from 'react-router-dom'
import { api } from '@/api'
import { isApiError } from '@/api/client'
import { PageHeader } from '@/components/shell/PageHeader'
import { Badge, ButtonLink, Card, EmptyState, ErrorState, InlineAlert, LoadingState } from '@/components/ui'
import { formatDate } from '@/lib/format'
import { useDocumentTitle } from '@/lib/hooks'
import { invalidateQueries, useQuery } from '@/lib/query'
import { OutreachBadge } from '../growth/signupShared'
import { OutreachCompose } from './OutreachCompose'
import { LeadCard, Organisation, Person, Timeline, WhereFrom } from './SignupCards'
import { signupKey, signupTarget } from './signupData'

export default function SignupDetailPage() {
  const { id: rawId } = useParams()
  const id = Number(rawId)
  const validId = Number.isInteger(id) && id > 0
  const { data: signup, error, status, refetch } = useQuery(validId ? signupKey(id) : null, (signal) => api.growth.signups.get(id, signal))
  const deleted = !!signup && signup.league.id === null
  const options = useQuery(deleted ? ['signups', 'outreach-options'] : null, (signal) => api.growth.outreach.options(signal))
  useDocumentTitle(signup ? signup.league.name : 'Tenant')

  if (!validId || (status === 'error' && isApiError(error) && error.code === 'not_found')) {
    return (
      <Card>
        <EmptyState
          title="Not found"
          description={validId ? `There's no signup with id ${id}.` : `“${rawId}” isn't a signup id.`}
          action={<ButtonLink to="/tenants">Back to tenants</ButtonLink>}
        />
      </Card>
    )
  }
  if (status === 'loading' && !signup) return <LoadingState label="Loading…" />
  if (!signup) {
    return (
      <Card>
        <ErrorState error={error} title="Couldn't load this tenant" onRetry={refetch} />
      </Card>
    )
  }
  // The league is live: it's a tenant like any other, and this is its Signup tab.
  if (signup.league.id !== null) return <Navigate to={`/tenants/${signup.league.id}/signup`} replace />

  const reload = () => invalidateQueries('signups')
  const l = signup.league

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        crumbs={[{ label: 'Tenants', to: '/tenants' }, { label: 'Deleted', to: '/tenants?view=deleted' }, { label: l.name }]}
        title={l.name}
        subtitle={<span className="break-words">{[l.subdomain, signup.person.name ?? signup.person.email].filter(Boolean).join(' · ')}</span>}
        badges={
          <>
            <Badge tone="neutral">Deleted</Badge>
            <OutreachBadge status={signup.outreach.status} />
            {signup.suppressed ? <Badge tone="bad">Do not contact</Badge> : null}
          </>
        }
      />
      <InlineAlert tone="info" title={l.deleted_at ? `League deleted on ${formatDate(l.deleted_at)}` : 'League deleted'}>
        {l.deleted_by === 'cleanup' ? 'The signup cleanup deleted it: it never paid. ' : l.deleted_by === 'ops' ? 'Deleted from Ops. ' : ''}
        Who signed up and every email are kept here.
      </InlineAlert>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <div className="grid min-w-0 content-start gap-4">
          <Person signup={signup} title="Signed up by" />
          {options.data ? (
            <OutreachCompose target={signupTarget(signup)} options={options.data} />
          ) : options.status === 'error' ? (
            <ErrorState error={options.error} title="Couldn't load the compose panel" onRetry={options.refetch} />
          ) : null}
          <Timeline signup={signup} onNote={reload} />
        </div>
        <div className="grid min-w-0 content-start gap-4">
          <WhereFrom signup={signup} />
          <Organisation signup={signup} />
          <LeadCard signup={signup} onAdded={reload} />
        </div>
      </div>
    </div>
  )
}
